import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { DtoApplication } from "@porchpass/api-client";
import { useWebClientApi } from "src/api";
import { useAuth, useUser } from "src/context";
import { logSentryError, logSentryMessage } from "src/utils";
import { generic_page_error_message } from "src/constants";
import { setApplicationAndRouteUser } from "./helpers";

export const DashboardInitializer = ({
  children,
}: {
  children: (props: {
    applications: DtoApplication[] | null;
    isLoading: boolean;
    error: string | null;
  }) => React.ReactNode;
}) => {
  const { dealerName: rawDealerName } = useParams();
  const dealerName = rawDealerName?.trim();
  const { setUserData } = useUser();
  const api = useWebClientApi();
  const { user: firebaseUser } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<DtoApplication[] | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    void initializeApplicationFlow();

    return () => {
      cancelledRef.current = true;
    };
  }, [firebaseUser]);

  const initializeApplicationFlow = async (retriesLeft = 5) => {
    if (!firebaseUser || !dealerName || cancelledRef.current) return;

    let data: DtoApplication[] | null = null;

    try {
      data = await api.getApplications(dealerName);
    } catch (err) {
      if (cancelledRef.current) {
        console.log("Dashboard initialization aborted due to reroute.");
        return;
      }

      const message = err instanceof Error ? err.message : String(err);
      console.log("Error fetching applications:", message);
      logSentryMessage(message, "dashboard", "get-applications-initial");
      return;
    }

    if (cancelledRef.current) return;

    const hasNoApplications = data instanceof Array && data.length === 0;
    if (hasNoApplications) {
      console.log(
        "No application found for this user, redirecting to register page.",
      );
      setUserData?.({ dealer_id: dealerName });
      navigate(`/${dealerName}/register`);
      return;
    }

    const hasAtLeastOneApplication = data?.[0] && data?.[0]?.id;
    if (hasAtLeastOneApplication) {
      setApplicationAndRouteUser(data, {
        setUserData,
        firebaseUser,
        dealerName,
        navigate,
        setIsLoading,
        setApplications: (applications) => setApplications(applications),
      });
    } else if (retriesLeft > 0) {
      retryApplicationsFetch(retriesLeft);
    } else {
      handleMaxRetriesError();
    }
  };

  const retryApplicationsFetch = (retriesLeft: number) => {
    console.log(
      `Retrying application ID fetch... (${retriesLeft - 1} retries left)`,
    );
    logSentryMessage(
      `Retrying application fetch. Retries left: ${retriesLeft - 1}`,
      "dashboard",
      "retrying-application-fetch",
    );
    setTimeout(() => void initializeApplicationFlow(retriesLeft - 1), 1500);
  };

  const handleMaxRetriesError = () => {
    console.error(
      "No application_id found after retries. Not retrying further.",
    );
    logSentryError(
      new Error("Max retries exceeded: No application_id found."),
      "dashboard",
      "max-retries-exceeded",
    );
    setError(
      `Unable to load applications. Refresh the page to try again. ${generic_page_error_message}`,
    );
    setIsLoading(false);
  };

  return <>{children({ applications, isLoading, error })}</>;
};
